import { InputField } from "@/components/common/input";
import { IAddressAutocompleteFields } from "@/components/common/addressInput/common";
import { AddressAutocompleteInput } from "@/components/common/addressInput";
import { SelectField } from "@/components/common/select";
import {
  firstNameField,
  phoneField,
  secondNameField,
  zipCodeField,
} from "@/pages/quiz/base/questions/contactField/validationRules";
import { omit } from "lodash";
import CustomCheckbox from "@/pages/quiz/base/questions/disclaimerAcknowledgment/components/CustomCheckbox";
import { FRONT_TERMS_LINKS } from "@/pages/quiz/base/questions/contactField/common";
import { ContactLink } from "@/pages/quiz/base/questions/contactField/components/contactLink";
import clsx from "clsx";
import { useEffect, useState } from "react";
import { Button, ButtonType } from "@/components/common/button";
import { QuizQuestionProps } from "@/pages/quiz/context/types";
import { useManageInputFields } from "@/pages/quiz/hooks/useManageInputFields";
import { availableStateOptions } from "@/pages/quiz/base/questions/selectState";
import { usePrefillQuestionValues } from "@/pages/quiz/hooks/usePrefillQuestionValues";
import { v4 } from "uuid";

const NUMBER_OF_ANSWERS = 7;

type ValidationRule = {
  required?: boolean;
  minLength?: number;
  maxLength?: number;
  regex?: RegExp;
  regexFailMessage?: string;
};

const validationFields: Record<FormFieldName, ValidationRule> = {
  firstName: firstNameField,
  lastName: secondNameField,
  address: {
    required: true,
    minLength: 2,
    maxLength: 50,
  },
  address2: {
    minLength: 2,
    maxLength: 50,
  },
  city: {
    required: true,
    minLength: 2,
    maxLength: 50,
  },
  zipCode: zipCodeField,
  state: {
    required: true,
  },
  phone: phoneField,
};

type FormFieldName =
  | "address"
  | "address2"
  | "city"
  | "zipCode"
  | "state"
  | "phone"
  | "firstName"
  | "lastName";

type AddressFieldsValues = Record<FormFieldName, string> & {
  acceptTerms?: boolean;
};

const ContactForAvailablePlansAddress = ({
  question,
  questionValue,
  setQuestionValue,
  setQuestionAnswer,
}: QuizQuestionProps<AddressFieldsValues>) => {
  const [isShowAnimation, setShowAnimation] = useState(!!questionValue);
  useEffect(() => {
    if (!!questionValue["address"]?.length) {
      setShowAnimation(true);
    }
  }, [questionValue]);

  const currentNumberOfAnswers = NUMBER_OF_ANSWERS;

  const { registerField, error, validateField, setFieldError, onValidateForm } =
    useManageInputFields(
      {
        setQuestionValue,
        questionValue: omit(questionValue, "acceptTerms"),
      },
      validationFields
    );

  usePrefillQuestionValues({
    fields: ["phone", "firstName", "lastName"],
    setValue: (values) => {
      setQuestionValue({ ...questionValue, ...values });

      Object.entries(values).forEach(([key, value]) => {
        setFieldError(key, validateField(key as FormFieldName, value ?? ""));
      });
    },
  });

  const isAllAnswered =
    Object.entries(questionValue).filter(
      ([key, value]) =>
        key !== "address2" &&
        key !== "acceptTerms" &&
        typeof value === "string" &&
        !!value.trim()
    ).length >= currentNumberOfAnswers;

  const isNoErrors = Object.values(error).every(
    (errorMessage) => !errorMessage
  );

  const isFormAnswered = isAllAnswered && isNoErrors;

  const onSubmit = () => {
    if (isFormAnswered) {
      setQuestionAnswer(questionValue);
    }
  };

  const handleAddressInput = async (
    _: string,
    addressData: IAddressAutocompleteFields
  ) => {
    const newAddressFields: Partial<AddressFieldsValues> = {};
    Object.entries(addressData).forEach(async ([key, value]) => {
      if (["city", "state", "zipCode", "address", "address2"].includes(key)) {
        newAddressFields[key as FormFieldName] = value;

        registerField(key as any).onChange({
          target: { name: key, value },
        } as any);
      }
    });

    setQuestionValue({ ...questionValue, ...newAddressFields });
  };

  const handleCheckbox = () =>
    setQuestionValue({
      ...questionValue,
      acceptTerms: !questionValue?.acceptTerms,
    });

  return (
    <div className="w-full flex-col flex gap-2">
      <AddressAutocompleteInput
        addressValue={questionValue["address"]}
        onChange={handleAddressInput}
        errorMessages={error.address}
        onBlur={(e) => {
          const event = { ...e, target: { ...e.target, name: "address" } };
          registerField("address").onBlur(event);
        }}
      />
      {isShowAnimation && (
        <div className={clsx("animate-fade-in-500", "flex-col flex gap-2")}>
          <InputField
            {...registerField("address2")}
            label="Address line 2 - optional"
          />

          <InputField {...registerField("city")} label="City" />

          <div className="flex gap-4">
            {
              // @ts-ignore
              <SelectField
                // @ts-ignore
                {...registerField("state")}
                options={availableStateOptions}
                label="State"
              />
            }
            <InputField {...registerField("zipCode")} label="Zip Code" />
          </div>

          <InputField
            {...registerField("phone")}
            label="Phone number"
            type="phone"
            placeholder="(888) 888-8888"
          />
        </div>
      )}

      <div className="w-full flex gap-4 flex-row">
        <InputField
          {...registerField("firstName")}
          placeholder="John"
          label="First name"
        />
        <InputField
          {...registerField("lastName")}
          placeholder="Doe"
          label="Last name"
        />
      </div>
      <p className="text-xs text-gray-dark font-bold">
        * Enter your full name exactly as it appears on your government-issued
        photo ID.
      </p>

      <Button
        disabled={!isFormAnswered}
        onClick={onValidateForm(onSubmit)}
        type={ButtonType.CONTINUE}
        className="!mt-[24px]"
      />

      <CustomCheckbox
        onChange={handleCheckbox}
        isChecked={questionValue?.acceptTerms || false}
        name="acceptTerms"
        label={
          <div className="text-sm font-bold -ml-4">
            Text me when my prescription is approved & shipped!
          </div>
        }
      />

      <div className="mt-0 text-gray-dark font-medium text-[10px]">
        By checking the options "Text me when my prescription is approved &
        shipped!" I agree to receive automatically-generated marketing text
        messages from SkinnyRx at my number I provided above. Consent not a
        condition of purchase. Msg&data rates may apply. Reply HELP or STOP to
        opt out. See our{" "}
        <ContactLink
          href={FRONT_TERMS_LINKS.TEXT_MESSAGE_TERMS}
          text="Text Msg"
        />
        , <ContactLink href={FRONT_TERMS_LINKS.PRIVACY} text="Privacy Policy" />
        {"  "}&{" "}
        <ContactLink href={FRONT_TERMS_LINKS.TERMS} text="Terms of Service" />.
      </div>
    </div>
  );
};

export { ContactForAvailablePlansAddress };
